import { inject, Injectable } from "@angular/core";
import { AttributeInfoDto, LayerIconDto, LayerStylesDto } from "@cloud-twin-v2/generated/simulation.generated.client";
import { BaseService } from "@common-modules/shared/base.service";
import { ArrayHelperService } from "@common-modules/shared/helpers/array-helper.service";
import { DateHelperService } from "@common-modules/shared/helpers/date-helper.service";
import { MapDateRange } from "@common-modules/wlm-map/models/map-date-range";
import { asEnumerable } from "linq-es2015";
import { map, Observable, ReplaySubject, tap } from "rxjs";
import { CtNetworkElementType } from './../network-elements/ct-network-element-type';
import { CtMetadata } from "./ct-metadata";

export type TMetadataByScenarioAndAttributeName = Map<string, Map<string, AttributeInfoDto>>;

@Injectable({ providedIn: 'root' })
export class CtAttributesService extends BaseService {
  
  private _metadataByAttributeName$ = new ReplaySubject<TMetadataByScenarioAndAttributeName>(1);
  readonly metadataByAttributeName$ = this._metadataByAttributeName$.asObservable();
  private _metadata$ = new ReplaySubject<CtMetadata>(1);
  readonly metadata$ = this._metadata$.asObservable();
  private readonly _dateHelper = inject(DateHelperService);
  private readonly _arrayHelper = inject(ArrayHelperService);

  load(scenarioIds: string[]): Observable<any> {
    return this.getAttributesMetadata(scenarioIds).pipe(tap(metadata => {
      this._metadataByAttributeName$.next(this.getMetadataByScenarioAndAttributeName(metadata));
      this._metadata$.next(metadata);
    }));
  }

  getNetworkElementTypes(): Observable<CtNetworkElementType[]> {
    return this.metadata$.pipe(map(metadata => {
      let result = asEnumerable(Object.values(metadata))
        .SelectMany(elementTypeCategory => elementTypeCategory)
        .Select(elementTypeCategory => {
          const net: CtNetworkElementType = {
            networkElementTypeId: elementTypeCategory.type,
            networkElementTypeName: elementTypeCategory.type,
          };
          return net;
        }).ToArray();

      result = this._arrayHelper.onlyUniqueByFn(result, (a, b) =>
        a.networkElementTypeId === b.networkElementTypeId
      );

      return result;
    }));
  }

  getStylesByNEType(): Observable<Map<string, LayerStylesDto[]>> {
    return this.metadata$.pipe(map(metadata => {
      const result: Map<string, LayerStylesDto[]> = asEnumerable(Object.values(metadata))
        .SelectMany(elementTypeCategory => elementTypeCategory)
        .GroupBy(elementTypeCategory => elementTypeCategory.type)
        .ToMap(group => group.key, group => group.map(item => item.layerStyle).filter(item => item));
      return result;
    }));
  }

  getIconsByNEType(): Observable<Map<string, LayerIconDto[]>> {
    return this.metadata$.pipe(map(metadata => {
      const result: Map<string, LayerIconDto[]> = asEnumerable(Object.values(metadata))
        .SelectMany(elementTypeCategory => elementTypeCategory)
        .Select(elementTypeCategory => elementTypeCategory.layerIcon)
        .Where(layerIcon => layerIcon != null)
        .GroupBy(layerIcon => layerIcon.layerType)
        .ToMap(group => group.key);
      return result;
    }));
  }

  getResultLoadDateRange(currentDate: Date, dateRange: MapDateRange): {startDate: Date; endDate: Date} {
    const resultStepslimit = 0;
    const selectedDate = currentDate.getTime();
    let startDate: Date;
    let endDate: Date;
    const totalSteps = dateRange.steps.length;
    const lastStepIndex = totalSteps -1;
    
    if (dateRange.startDate.getTime() === selectedDate){
      startDate = dateRange.startDate;
      endDate = (resultStepslimit < totalSteps) ? dateRange.steps[resultStepslimit] : dateRange.steps[lastStepIndex];
    }else if (dateRange.endDate.getTime() === selectedDate){
      const startDateIndex = (lastStepIndex - resultStepslimit) >= 0 ? lastStepIndex - resultStepslimit : 0;
      startDate = dateRange.steps[startDateIndex];
      endDate = dateRange.endDate;
    }else{
      const selectedDateIndex = dateRange.steps.findIndex(date => date.getTime() === selectedDate);
      const startDateIndex = (selectedDateIndex - resultStepslimit) >= 0 ? selectedDateIndex - resultStepslimit : 0;
      const endDateIndex = (selectedDateIndex + resultStepslimit) <= lastStepIndex ? selectedDateIndex + resultStepslimit : lastStepIndex;
      startDate = dateRange.steps[startDateIndex];
      endDate = dateRange.steps[endDateIndex];
    }

    return {startDate, endDate};
  }

  private getAttributesMetadata(scenarioIds: string[]): Observable<CtMetadata> {
    return this.httpCacheClient.get<CtMetadata>(`${this.apiUrl}/attribute/metadata`, null, { scenarioIds });
  }

  private getMetadataByScenarioAndAttributeName(metadata: CtMetadata): TMetadataByScenarioAndAttributeName {
    const result = new Map<string, Map<string, AttributeInfoDto>>();
    for (let [scenarioId, elementTypeCategories] of Object.entries(metadata)) {
      const item = asEnumerable(elementTypeCategories)
        .SelectMany(item => item.categories)
        .SelectMany(item => item.attributes)
        .GroupBy(item => item.name)
        .ToMap(group => group.key, group => group.length ? group[0] : null);

      result.set(scenarioId, item);
    }

    return result;
  }

  getDateRange(scenarioIds: string[]): Observable<MapDateRange> {
    return this.httpCacheClient.get<MapDateRange>(`${this.apiUrl}/attribute/date-range`, null, { scenarioIds }).pipe(map(dateRange => {
      this.ensureDateObjects(dateRange);
      return dateRange;
    }));
  }

  private ensureDateObjects(dateRange: MapDateRange): void {
    const ensureDate = this._dateHelper.ensureDateObject;
    if (dateRange) {
      dateRange.startDate = ensureDate(dateRange.startDate);
      dateRange.endDate = ensureDate(dateRange.endDate);
      dateRange.steps = dateRange.steps.map(step => ensureDate(step));
    }
  }
}
